'use client';

import React, { useEffect } from 'react';
import {
  AmplitudeActions,
  AmplitudeEvents,
  AmplitudeProperties,
} from 'whoop-analytics/lib/types';
import { whoopAnalyticsScrollTracker } from 'whoop-analytics/lib/sources/whoop-analytics';
import { useShallow } from 'zustand/react/shallow';
import type { JoinFlowContent } from 'services/generated/growth-content-service/models/JoinFlowContent';
import type { CheckoutPromo } from 'services/generated/membership-service-v2/models/CheckoutPromo';
import { createClientAuth } from '@whoop/auth-client';
import type { ParsedCartDTO } from 'ui/types/cartTypes';
import { useStripe } from '@stripe/react-stripe-js';
import { getOneTouchDigitalWalletOptionsV2 } from 'ui/utils/paymentHelpers';
import { env } from 'env';
import { usePathname } from 'next/navigation';
import { getFirstVariant, LoadingOverlay } from 'ui';
import type { Product } from 'services/generated/commerce-service/models/Product';
import { getPriceValues } from 'ui/utils/variantHelpers';
import JFOplContent from './JFOplContent/JFOplContent';
import { useAnalytics } from '@/hooks/analytics';
import { useSiteStore } from '@/store/SiteStoreProvider';
import type { PromoType } from '@/lib/utils/initialDataFetcher';
import {
  EXPERIMENT_JF_RAF_PERSISTENCE,
  EXPERIMENT_JF_TRIAL_BLOCK,
  EXPERIMENT_TRUEDMED_PAYMENTS,
  JF_EXPRESS_CHECKOUT,
  JF_VERTICAL_STACK,
} from '@/lib/constants/experiments';
import type { SiteWidePromoContent } from '@/types/siteWidePromoTypes';
import { getStripeAccount } from '@/stripe/stripeUtils';
import { useNavigation } from '@/hooks/navigation';
import type { GenericItem } from '@/client/contentfulClient';
import type {
  MembershipTierInfo,
  WhatsIncludedTierInfo,
} from '@/types/maverickTypes';
import Cart from '@/components/Cart/Cart';
import {
  MembershipTier,
  type CollapsibleInfoSubsection,
  type ContentfulDisclaimer,
  type MembershipTierComparisonFeatureList,
  type PageDisclaimer,
  TRIAL,
} from '@/lib/constants/maverick';
import {
  getMembershipByAttributes,
  getTierCompatibleProducts,
} from '@/lib/utils/maverickHelpers';
import {
  type IFeatureAvailability,
  FeatureAvailability,
} from '@/hooks/featureAvailabilityClass';
import type { IBanner } from '@/lib/utils/promoBannerHelpers';
import { setExperimentCookieOnClient } from '@/lib/utils/analyticsHelper';

interface OplPageProps {
  featureFlags?: Record<string, boolean>;
  returnShippingCost?: number;
  memberships?: Product[];
  availableTiers: MembershipTierInfo[];
  trialMembership?: Product;
  promoContent?: JoinFlowContent;
  promo?: CheckoutPromo;
  sitewidePromoContent?: SiteWidePromoContent;
  promoType: PromoType;
  skipMemSelection?: string;
  skipMemSelectionEntry?: string;
  existingCart?: ParsedCartDTO;
  joinTheNextGenContent?: GenericItem | undefined;
  trialNowChangeLaterContent?: GenericItem[] | undefined;
  familyPlansContent?: GenericItem[] | undefined;
  whatsIncludedContent?: WhatsIncludedTierInfo[] | undefined;
  collapsibleSubsections?: CollapsibleInfoSubsection[];
  membershipTierFeatureLists?: MembershipTierComparisonFeatureList[];
  disclaimers?: ContentfulDisclaimer[];
  pageDisclaimers?: PageDisclaimer[];
  featureAvailability?: IFeatureAvailability[];
  isMobile: boolean;
  children: React.ReactNode;
  regionalBanners?: IBanner[];
  trialBlockVariant: string | null;
  verticalStackVariant: string | null;
  experimentUUID: string;
  experiments?: Record<string, 'experiment' | 'control' | null>;
  promoCode?: string;
}

export default function JFOplPage({
  promoCode,
  featureFlags,
  returnShippingCost,
  availableTiers,
  memberships,
  trialMembership,
  promo,
  promoContent,
  promoType,
  sitewidePromoContent,
  skipMemSelection,
  skipMemSelectionEntry,
  existingCart,
  joinTheNextGenContent,
  trialNowChangeLaterContent,
  familyPlansContent,
  whatsIncludedContent,
  collapsibleSubsections,
  membershipTierFeatureLists,
  disclaimers,
  pageDisclaimers,
  featureAvailability,
  isMobile,
  children,
  regionalBanners,
  trialBlockVariant,
  verticalStackVariant,
  experimentUUID,
  experiments,
}: OplPageProps): JSX.Element {
  const {
    setFeatureFlags,
    initializePromo,
    setReturnShippingCost,
    setIsRafGiftFlow,
    setPromoType,
    setEcommCart,
    setSkipMemSelectFlow,
    country,
    currency,
    setStripePaymentRequest,
    cart,
    setStripeAccount,
    setPeakAnnualOriginalPrice,
    setFeatureAvailability,
    setRegionalBanners,
    setExperimentVariant,
  } = useSiteStore(
    useShallow((state) => ({
      setFeatureFlags: state.setFeatureFlags,
      initializePromo: state.initializePromo,
      setReturnShippingCost: state.setReturnShippingCost,
      setIsRafGiftFlow: state.setIsRafGiftFlow,
      setPromoType: state.setPromoType,
      currency: state.currency,
      setEcommCart: state.setEcommCart,
      setSkipMemSelectFlow: state.setSkipMemSelectFlow,
      country: state.country,
      setStripePaymentRequest: state.setStripePaymentRequest,
      cart: state.cart,
      setStripeAccount: state.setStripeAccount,
      setPeakAnnualOriginalPrice: state.setPeakAnnualOriginalPrice,
      setFeatureAvailability: state.setFeatureAvailability,
      setRegionalBanners: state.setRegionalBanners,
      setExperimentVariant: state.setExperimentVariant,
    })),
  );
  // Only set the rafPersistenceExperimentVariant if there is a promo code
  const rafPersistenceExperimentVariant = promoCode
    ? experiments?.[EXPERIMENT_JF_RAF_PERSISTENCE]
    : null;
  const truemedExperimentVariant = experiments?.[EXPERIMENT_TRUEDMED_PAYMENTS];

  const { goTo } = useNavigation();
  const pathname = usePathname();
  const stripe = useStripe();
  const auth = createClientAuth({
    env: env.NEXT_PUBLIC_ENV,
    segmentKey: env.NEXT_PUBLIC_SEGMENT_API_KEY,
  });
  const {
    initialize: initializeAnalytics,
    isInitialized,
    trackPageView,
    trackAmplitudeEvent,
    setAmplitudeUserProperty,
  } = useAnalytics();

  useEffect(() => {
    setExperimentCookieOnClient(experimentUUID);
    if (isInitialized) {
      rafPersistenceExperimentVariant &&
        setAmplitudeUserProperty(
          EXPERIMENT_JF_RAF_PERSISTENCE,
          rafPersistenceExperimentVariant,
        );
      trialBlockVariant &&
        setAmplitudeUserProperty(EXPERIMENT_JF_TRIAL_BLOCK, trialBlockVariant);

      verticalStackVariant &&
        setAmplitudeUserProperty(JF_VERTICAL_STACK, verticalStackVariant);

      truemedExperimentVariant &&
        setAmplitudeUserProperty(
          EXPERIMENT_TRUEDMED_PAYMENTS,
          truemedExperimentVariant,
        );

      if (skipMemSelection) {
        trackAmplitudeEvent(AmplitudeEvents.ViewedJoinFlow, {
          action: AmplitudeActions.SkipMemSelect,
          skip_mem_select: skipMemSelection,
          skip_mem_select_entry: skipMemSelectionEntry,
        });
      } else {
        const maybeFamilyMembership = getMembershipByAttributes({
          memberships: memberships ?? [],
          trialDays: 365,
          isFamily: true,
        });
        const label = maybeFamilyMembership
          ? AmplitudeProperties.FamilyMembershipVisible
          : undefined;
        trackAmplitudeEvent(AmplitudeEvents.ViewedJoinFlow, {
          action: AmplitudeActions.Landing,
          label,
        });
      }
    }
  }, [isInitialized]);

  useEffect(() => {
    if (experiments) {
      Object.entries(experiments).forEach(([experiment, variant]) => {
        if (variant) {
          setExperimentVariant(experiment, variant);
        }
      });
    }
  }, [experiments]);

  const initializeStripePaymentOptions = async () => {
    if (!stripe) return;
    const options = getOneTouchDigitalWalletOptionsV2({
      cart,
      currency,
      stripeCountry: '',
    });

    const stripeAccount = getStripeAccount(country);
    const request = stripe.paymentRequest({
      ...options,
      // This is for the region that our stripe account is setup in. Not the users country.
      country: stripeAccount === 'EU' ? 'IE' : 'US',
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
      requestShipping: true,
    });

    const paymentRequestOptions = await request.canMakePayment();
    if (!paymentRequestOptions) {
      setAmplitudeUserProperty('express-checkout-available', 'false');
      setAmplitudeUserProperty(JF_EXPRESS_CHECKOUT, 'none');
      setStripePaymentRequest();
      return;
    }

    setStripePaymentRequest({
      request,
      paymentOptionsAvailable: paymentRequestOptions,
    });
  };

  // Initialize peak annual price if memberships are available (used in faqs and learn more modal)
  const initializePeakAnnualOriginalPrice = () => {
    if (!memberships) return;

    // Get the annual membership for the peak tier
    const peakMemberships = getTierCompatibleProducts(
      memberships,
      MembershipTier.PEAK,
    );
    const peakAnnualMembership = getMembershipByAttributes({
      memberships: peakMemberships ?? [],
      trialDays: 365,
      isFamily: false,
    });

    if (!peakAnnualMembership) return;

    const peakPrice = getPriceValues(getFirstVariant(peakAnnualMembership));
    setPeakAnnualOriginalPrice(peakPrice.originalPrice ?? 0);
  };

  async function init(): Promise<void> {
    // If a token exists, it will be validated when the user gets to the gift page in middleware
    const isRafGift = Boolean(auth.getToken()) && pathname.includes('/gift/');
    setIsRafGiftFlow(isRafGift);
    // clear auth cookies if not in raf gift flow
    async function clearAuthCookies() {
      await auth.signOut();
    }
    if (!isRafGift) {
      clearAuthCookies();
    }
    if (existingCart) {
      setEcommCart(existingCart);
    }
    setFeatureFlags(featureFlags ?? {});
    setReturnShippingCost(returnShippingCost);
    setPromoType(promoType);
    setStripeAccount(getStripeAccount(country));
    initializePromo(promoContent, promo, sitewidePromoContent);
    await initializeAnalytics();
    initializePeakAnnualOriginalPrice();
    trackPageView('OPL');
    setFeatureAvailability(new FeatureAvailability(featureAvailability ?? []));

    // Must be last so analytics/promo content/FF are initialized before redirect
    handleSkipMemSelect();
  }

  const handleSkipMemSelect = () => {
    if (skipMemSelection) {
      /**
       * We only want to skip the membership page if the user is coming from a trial skip mem selection
       * The skip mem selector flow boolean handles that skipping, so only set it if trial
       */
      if (skipMemSelection === TRIAL) {
        setSkipMemSelectFlow(skipMemSelection);
      }
      goTo(`bands?tier=${skipMemSelection}`);
    }
  };

  useEffect(() => {
    if (regionalBanners) {
      setRegionalBanners(regionalBanners);
    }

    async function initAndSetupListeners() {
      await init();

      document.addEventListener('scroll', () => {
        whoopAnalyticsScrollTracker(trackAmplitudeEvent);
      });
    }

    initAndSetupListeners();

    return () => {
      document.removeEventListener('scroll', () => {
        whoopAnalyticsScrollTracker(trackAmplitudeEvent);
      });
    };
  }, []);

  useEffect(() => {
    initializeStripePaymentOptions();
  }, [stripe]);

  return (
    <div className='bg-white'>
      {skipMemSelection ? (
        <LoadingOverlay />
      ) : (
        <>
          <JFOplContent
            availableTiers={availableTiers}
            collapsibleSubsections={collapsibleSubsections}
            disclaimers={disclaimers}
            familyPlansContent={familyPlansContent}
            isMobile={isMobile}
            joinTheNextGenContent={joinTheNextGenContent}
            membershipTierFeatureLists={membershipTierFeatureLists}
            memberships={memberships}
            pageDisclaimers={pageDisclaimers}
            trialBlockVariant={trialBlockVariant}
            trialMembership={trialMembership}
            trialNowChangeLaterContent={trialNowChangeLaterContent}
            verticalStackVariant={verticalStackVariant}
            whatsIncludedContent={whatsIncludedContent}
          >
            {children}
          </JFOplContent>
          <Cart CTAButtonsVariant='continueToCheckout' />
        </>
      )}
    </div>
  );
}
